// コネクタ（接続矢印）を空白へドロップしたときに、接続元と同じ種別のオブジェクトを
// 矢印の先へ自動作成するための決定的ロジック。
//
// この層はストア・DOM・id 生成器に一切触れない（import 先も純関数と定数だけに限り、
// モジュール読み込みでストアが実体化されないようにする）。
// グリッド吸着の有効 / 無効、新規 id、現在のオブジェクト一覧はすべて引数で受け取る
// （呼び出し側の canvas-connector-overlay が注入する）。
// これにより単体テストでストアをモックせず、入力と出力だけで挙動を固定できる。
// 依存方向は「overlay → このモジュール」の一方向に保つ（循環依存を作らない）。

import {
  CONNECTOR_STUB_PX,
  clampFrameToCanvas,
  connectorStoredFrame,
  frameAABB,
  isConnectorEndpointObject,
  nearestSide,
  sideAnchor,
  sideUnitVector,
  type CanvasFrame,
  type CanvasObject,
  type ConnectorFrame,
  type ConnectorPoint,
  type ConnectorSide,
} from "@refinement-canvas/domain";
import type { CanvasObjectDraft } from "./use-canvas-op-dispatcher";
import { snapToGrid } from "../../lib/canvas-coordinates";
import { CONNECTOR_TARGET_HIT_PAD_PX } from "../../lib/connector-geometry";
import { textInitialHeight } from "./object-placement-defaults";
import { DEFAULT_TEXT_FONT_SIZE } from "../../lib/text-size-preference";

/**
 * 新規オブジェクトと接続元の外接矩形の間に確保する最小間隔（ステージ座標 px）。
 *
 * CONNECTOR_STUB_PX（24）は「コネクタのグリッド迂回が成立する最小すき間」であり、
 * これを下回ると経路探索が外側へ抜けられず矢印が図形の縁に接する（README「コネクタの既知の制限 (1)」）。
 * その境界にぴったり張り付かないよう 8px の余裕を足した値を使う。
 */
export const AUTO_CREATE_GAP_PX = CONNECTOR_STUB_PX + 8;

/** 自動作成の対象となる種別。接続元の種別と 1 対 1 で対応する。 */
export type AutoCreateKind = "sticky" | "text" | "shape";

interface AutoCreateSize {
  readonly w: number;
  readonly h: number;
}

/**
 * 自動作成の対象種別なら種別を、対象外（画像・動画・リンクカード・スタンプ、
 * そもそも接続元になれない種別、undefined）なら null を返す。
 *
 * 画像・動画・リンクカードは src / url を持たない空オブジェクトが破綻するため、
 * スタンプは「同じ絵文字が増えるだけ」で発想の展開にならないため対象外とする。
 */
export function autoCreateKindOf(object: CanvasObject | undefined): AutoCreateKind | null {
  if (!object) return null;
  switch (object.type) {
    case "sticky":
      return "sticky";
    case "text":
      return "text";
    case "shape":
      return "shape";
    case "image":
    case "video":
    case "link-card":
    case "stamp":
    case "pen-stroke":
    case "connector":
    case "comment-thread":
    case "section":
    case "table":
      return null;
    default: {
      // 種別が増えたときにコンパイルエラーで気づけるようにする（網羅チェック）。
      const exhaustive: never = object;
      return exhaustive;
    }
  }
}

/** オブジェクトの枠（回転込み）を幾何計算用の形へ変換する。 */
function frameOf(object: CanvasObject): ConnectorFrame {
  return { x: object.x, y: object.y, w: object.w, h: object.h, rotation: object.rotation ?? 0 };
}

/** 矩形を四方へ pad だけ広げる。 */
function inflate(frame: CanvasFrame, pad: number): CanvasFrame {
  return { x: frame.x - pad, y: frame.y - pad, w: frame.w + pad * 2, h: frame.h + pad * 2 };
}

/** 点が矩形（pad で拡張）の内側にあるか。 */
function containsPoint(frame: CanvasFrame, point: ConnectorPoint, pad: number): boolean {
  const box = inflate(frame, pad);
  return (
    point.x >= box.x && point.x <= box.x + box.w && point.y >= box.y && point.y <= box.y + box.h
  );
}

/** 2 つの矩形が重なっているか（辺で接するだけは重なりとしない）。 */
function overlaps(a: CanvasFrame, b: CanvasFrame): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * 自動作成を抑止すべきか。抑止条件は次の 3 つ（B2 / B3 / B4 は設計時の識別子）。
 *
 * - B2: 接続元が存在しない / ロック中 / 自動作成の対象外種別
 * - B3: ドロップ点が接続元の外接矩形（回転考慮）の内側 = 自分の上に重ねて作る事故
 * - B4: ドロップ点が「接続先になれる種別」のオブジェクトの外接矩形の内側
 *       ロック中のものも、回転により接続吸着（未回転 AABB 判定）が拾えなかったものも含めて抑止する。
 *       利用者はそのオブジェクトへ接続しようとしているため、重ねて新規作成しない。
 *       セクション・ペンストローク・コネクタ・コメントスレッドは接続先になれない種別のため
 *       抑止対象に含めない（それらの上へ発想を伸ばすのは通常の操作）。
 *
 * 接続先（draft.target）が取れている場合は呼び出し側が従来どおりのコネクタ作成へ分岐するため、
 * この関数は「接続先が無い状態での確定」でのみ呼ばれる。
 */
export function isAutoCreateBlocked(args: {
  readonly source: CanvasObject | undefined;
  readonly dropPoint: ConnectorPoint;
  readonly objects: Readonly<Record<string, CanvasObject>>;
}): boolean {
  const { source, dropPoint, objects } = args;
  if (!source) return true;
  if (source.locked) return true;
  if (autoCreateKindOf(source) === null) return true;
  if (containsPoint(frameAABB(frameOf(source)), dropPoint, CONNECTOR_TARGET_HIT_PAD_PX))
    return true;
  for (const object of Object.values(objects)) {
    if (object.id === source.id) continue;
    if (!isConnectorEndpointObject(object)) continue;
    if (containsPoint(frameAABB(frameOf(object)), dropPoint, CONNECTOR_TARGET_HIT_PAD_PX))
      return true;
  }
  return false;
}

/**
 * 新規オブジェクトのサイズ。原則として接続元から継承するが、テキストの高さだけは継承しない。
 * テキストの h は編集中に本文の実描画高さへ追随するため、長文テキストから伸ばすと
 * 「本文が空なのに極端に高いテキスト」が生まれてしまう。既存のテキスト配置と同じ式で算出する。
 */
function autoCreateSize(source: CanvasObject): AutoCreateSize {
  if (source.type === "text") {
    const fontSize = source.fontSize ?? DEFAULT_TEXT_FONT_SIZE;
    return { w: source.w, h: textInitialHeight(fontSize) };
  }
  return { w: source.w, h: source.h };
}

/**
 * 新規オブジェクトの配置矩形を求める。手順は次のとおり。
 *
 * 1. ドロップ点を中心に置く
 * 2. グリッド吸着が有効なら格子へ丸める
 * 3. 接続元（回転考慮 AABB を AUTO_CREATE_GAP_PX 広げたもの）と重なるなら押しのける
 * 4. NaN / 負サイズをサニタイズする
 *
 * 押しのけを吸着の「後」に置くのは、逆順だと吸着で間隔が最大 CANVAS_SNAP_PX / 2 縮み、
 * 「最終状態で AUTO_CREATE_GAP_PX 以上」という事後条件が崩れるため。
 * なお clampFrameToCanvas はキャンバス境界へのクランプではなく数値のサニタイズであり、
 * 本機能でも既存の配置と同じくキャンバス外への配置を許容する（座標は動かない）。
 */
export function computeAutoCreateFrame(args: {
  readonly source: CanvasObject;
  readonly fromSide: ConnectorSide;
  readonly dropPoint: ConnectorPoint;
  readonly gridSnapEnabled: boolean;
}): CanvasFrame {
  const { source, fromSide, dropPoint, gridSnapEnabled } = args;
  const { w, h } = autoCreateSize(source);
  const raw = { x: dropPoint.x - w / 2, y: dropPoint.y - h / 2, w, h };
  const snapped = gridSnapEnabled ? { x: snapToGrid(raw.x), y: snapToGrid(raw.y), w, h } : raw;
  return clampFrameToCanvas(pushOutOfSource(snapped, source, fromSide, dropPoint));
}

type PushAxis = "-x" | "+x" | "-y" | "+y";

/**
 * 接続元と重なる場合に、軸平行方向へ押しのける。
 *
 * 方向は「接続元の中心から見てドロップ点がある向き」を第一に採る。利用者が離した側へ出すのが
 * 意図に最も近いためである。移動量が最小の軸を選ぶ規則だけにすると、横長のオブジェクト
 * （既定サイズのテキスト 240x48 など）で右へ離したときに、上下方向の移動量のほうが小さいという
 * 理由だけで真上へ飛んでしまう。
 * 逆に接続辺の外向きへ固定すると、接続元の反対側で離したときに接続元の幅ぶん飛び越えてしまう。
 * ドロップ点が接続元の中心とほぼ一致する / 斜め 45 度ちょうどで向きが決まらない場合だけ、
 * 移動量が最小の軸 → 同値なら接続辺の外向き（回転を考慮した向きを最寄り軸へ吸着したもの）
 * → 配列の固定順、の順で決める。
 */
function pushOutOfSource(
  frame: CanvasFrame,
  source: CanvasObject,
  fromSide: ConnectorSide,
  dropPoint: ConnectorPoint
): CanvasFrame {
  const blocker = inflate(frameAABB(frameOf(source)), AUTO_CREATE_GAP_PX);
  if (!overlaps(frame, blocker)) return frame;

  const moves: ReadonlyArray<{
    readonly axis: PushAxis;
    readonly dx: number;
    readonly dy: number;
  }> = [
    { axis: "-x", dx: blocker.x - (frame.x + frame.w), dy: 0 },
    { axis: "+x", dx: blocker.x + blocker.w - frame.x, dy: 0 },
    { axis: "-y", dx: 0, dy: blocker.y - (frame.y + frame.h) },
    { axis: "+y", dx: 0, dy: blocker.y + blocker.h - frame.y },
  ];
  // 同値判定の許容誤差（回転考慮 AABB は三角関数由来の端数を持つため）。
  const EPSILON = 1e-6;
  const dropAxis = dropDirectionAxis(blocker, dropPoint, EPSILON);
  const preferred = dropAxis ?? preferredPushAxis(source, fromSide);
  const preferredMove = moves.find((move) => move.axis === preferred);
  // ドロップ点の向きが決まっているならその方向へ押す（利用者が離した側へ出す）。
  if (dropAxis !== null && preferredMove) {
    return {
      x: frame.x + preferredMove.dx,
      y: frame.y + preferredMove.dy,
      w: frame.w,
      h: frame.h,
    };
  }
  // 向きが決まらない場合だけ、移動量が最小の軸を選ぶ（同値なら接続辺の外向き → 配列の固定順）。
  let best = moves[0]!;
  let bestDistance = Math.abs(best.dx) + Math.abs(best.dy);
  for (const move of moves.slice(1)) {
    const distance = Math.abs(move.dx) + Math.abs(move.dy);
    if (distance < bestDistance - EPSILON) {
      best = move;
      bestDistance = distance;
      continue;
    }
    if (Math.abs(distance - bestDistance) <= EPSILON && move.axis === preferred) {
      best = move;
      bestDistance = distance;
    }
  }
  return { x: frame.x + best.dx, y: frame.y + best.dy, w: frame.w, h: frame.h };
}

/**
 * 接続元の中心から見て、ドロップ点がどの軸のどちら向きにあるか。
 * 支配的な成分が無い（中心とほぼ一致する）場合は null を返す。
 */
function dropDirectionAxis(
  blocker: CanvasFrame,
  dropPoint: ConnectorPoint,
  epsilon: number
): PushAxis | null {
  const dx = dropPoint.x - (blocker.x + blocker.w / 2);
  const dy = dropPoint.y - (blocker.y + blocker.h / 2);
  if (Math.abs(dx) <= epsilon && Math.abs(dy) <= epsilon) return null;
  if (Math.abs(dx) - Math.abs(dy) > epsilon) return dx >= 0 ? "+x" : "-x";
  if (Math.abs(dy) - Math.abs(dx) > epsilon) return dy >= 0 ? "+y" : "-y";
  // 斜め 45 度ちょうど。向きを一意に決められないため、接続辺の外向きの判断へ委ねる。
  return null;
}

/**
 * 同値時のタイブレークに使う軸。接続辺の外向き単位ベクトル（接続元の回転込み）を
 * 最寄りの軸へ吸着して求める。回転を無視すると、90 度 / 180 度回転した接続元で
 * 「接続辺の外向き」と直交・逆方向の軸を選んでしまう。
 */
function preferredPushAxis(source: CanvasObject, fromSide: ConnectorSide): PushAxis {
  const dir = sideUnitVector(fromSide, source.rotation ?? 0);
  if (Math.abs(dir.x) >= Math.abs(dir.y)) return dir.x >= 0 ? "+x" : "-x";
  return dir.y >= 0 ? "+y" : "-y";
}

/**
 * 「新規オブジェクト draft → コネクタ draft」の順の配列を返す。抑止条件に該当する場合は空配列。
 *
 * 順序には意味がある。サーバはコネクタ作成時に端点の実在を検証するため、
 * 端点図形の object.create をコネクタの object.create より先に送る必要がある
 * （貼り付け複製・Alt+ドラッグ複製・削除 Undo の復元と同じ不変条件）。
 */
export function buildAutoCreateDrafts(args: {
  readonly source: CanvasObject | undefined;
  readonly fromSide: ConnectorSide;
  readonly dropPoint: ConnectorPoint;
  readonly gridSnapEnabled: boolean;
  readonly newObjectId: string;
  readonly objects: Readonly<Record<string, CanvasObject>>;
}): readonly CanvasObjectDraft[] {
  const { source, fromSide, dropPoint, gridSnapEnabled, newObjectId, objects } = args;
  if (!source) return [];
  if (isAutoCreateBlocked({ source, dropPoint, objects })) return [];

  const frame = computeAutoCreateFrame({ source, fromSide, dropPoint, gridSnapEnabled });
  const created = buildObjectDraft(source, frame, newObjectId);
  if (!created) return [];

  const sourceFrame = frameOf(source);
  // 新規オブジェクトは回転を継承しないため rotation は 0。
  const newFrame: ConnectorFrame = { ...frame, rotation: 0 };
  const toSide = nearestSide(newFrame, sideAnchor(sourceFrame, fromSide));
  // 保存 bbox は createConnector と同じ導出にそろえる。サーバも同じ関数で再計算するため、
  // 楽観適用中のローカル値とサーバ確定値が一致する。
  const box = connectorStoredFrame(sourceFrame, fromSide, newFrame, toSide);
  const connector: CanvasObjectDraft = {
    type: "connector",
    from: { objectId: source.id, side: fromSide },
    to: { objectId: newObjectId, side: toSide },
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
  };
  return [created, connector];
}

/**
 * 接続元と同じ種別の新規オブジェクト draft を組み立てる。
 * 見た目（色・背景色・文字寄せ・文字サイズ・図形種別・塗り・線色・線幅）は接続元から継承し、
 * 本文は空、rotation / locked は継承しない（新規は未回転・未ロック）。
 */
function buildObjectDraft(
  source: CanvasObject,
  frame: CanvasFrame,
  newObjectId: string
): CanvasObjectDraft | null {
  const base = { id: newObjectId, x: frame.x, y: frame.y, w: frame.w, h: frame.h };
  switch (source.type) {
    case "sticky":
      return {
        ...base,
        type: "sticky",
        text: "",
        color: source.color,
        textAlign: source.textAlign,
      };
    case "text":
      return {
        ...base,
        type: "text",
        text: "",
        color: source.color,
        fill: source.fill,
        fontSize: source.fontSize,
        bold: source.bold,
        textAlign: source.textAlign,
      };
    case "shape":
      // arrowFromCorner / arrowFromEdge はドラッグ配置時の向き情報のため引き継がない
      //（既定の水平矢印になる）。線色・線幅は見た目の一部として引き継ぐ。
      return {
        ...base,
        type: "shape",
        shapeKind: source.shapeKind,
        fill: source.fill,
        stroke: source.stroke,
        // 旧図形の欠落値は実線を明示し、自動作成後の選好を一意にする。
        strokePattern: source.strokePattern ?? "solid",
        strokeWidth: source.strokeWidth,
      };
    default:
      return null;
  }
}
